import { FunctionComponent, ReactElement, cloneElement, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { filterBy } from '@progress/kendo-data-query';
import { MultiSelect, MultiSelectBlurEvent, MultiSelectChangeEvent, TagData } from '@progress/kendo-react-dropdowns';
import { useController } from 'react-hook-form';
import { styled } from 'styled-components';

import { MultiSelectNgFieldProps } from './MultiSelectNgFieldProps';

/**
 * Generic React Hook Form compatible MultiSelect component
 *
 * This component provides a reusable multi-select that integrates seamlessly with React Hook Form.
 * It supports any data type and can be customized for different use cases.
 *
 * @example
 * // Basic usage with locations
 * <MultiSelectNgField
 *   data={locations}
 *   name="selectedLocations"
 *   textField="name"
 *   dataItemKey="id"
 *   required
 * />
 *
 * @example
 * // Usage with users
 * <MultiSelectNgField
 *   data={users}
 *   name="selectedUsers"
 *   textField="email"
 *   dataItemKey="id"
 *   required
 *   requiredMessage="Please select at least one user"
 * />
 *
 * @template T - The type of data items
 */
export const MultiSelectNgField = <T extends Record<string, unknown>>({
  data,
  name,
  textField,
  dataItemKey,
  id,
  required = false,
  disabled = false,
  requiredMessage,
  validator,
  onChange,
  onBlur,
  onFilterChange,
  enableCustomTagRender = false,
  onTagClick,
  filterable,
  filterFields,
  ...rest
}: MultiSelectNgFieldProps<T>): ReactElement => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);
  const [filteredData, setFilteredData] = useState<T[]>([]);

  // Update filtered data when original data changes
  useEffect(() => {
    setFilteredData(data.slice());
  }, [data]);

  // Memoize validation rules to prevent recreation on every render
  const validationRules = useMemo(
    () => ({
      validate: validator,
      required: required ? requiredMessage || `${name} is required` : false,
    }),
    [validator, required, requiredMessage, name],
  );

  const {
    field: { onChange: rhfOnChange, onBlur: rhfOnBlur, value, ...fieldRest },
    formState,
    fieldState: { isTouched, invalid, error },
  } = useController({
    name,
    rules: validationRules,
  });

  const handleChange = useCallback(
    (event: MultiSelectChangeEvent) => {
      rhfOnChange(event.value);
      onChange?.(event);
    },
    [onChange, rhfOnChange],
  );

  const handleBlur = useCallback(
    (event: MultiSelectBlurEvent) => {
      rhfOnBlur();
      onBlur?.(event);
    },
    [onBlur, rhfOnBlur],
  );

  const handleFilterChange = useCallback(
    (event: any) => {
      if (filterable && event.filter) {
        setFilteredData(filterBy(data.slice(), event.filter));
      } else {
        setFilteredData(data.slice());
      }
      // Call custom filter change handler if provided
      onFilterChange?.(event);
    },
    [data, filterable, onFilterChange],
  );

  const onOpenDialog = useCallback(
    (tagData: TagData) => {
      const item = tagData.data[0];
      setSelectedItem(item);
      setShowDialog(true);
      onTagClick?.(item);
    },
    [onTagClick],
  );

  const tagRender = useCallback(
    (tagData: TagData, li: ReactElement<HTMLLIElement>) => {
      if (!enableCustomTagRender) {
        return li;
      }

      // Use the original approach but with better performance characteristics
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return cloneElement(li, li.props, [
        <span onClick={() => onOpenDialog(tagData)} key={tagData.data[0]?.[dataItemKey]} style={{ cursor: 'pointer' }}>
          {tagData.text}
        </span>,
        li.props.children,
      ]);
    },
    [enableCustomTagRender, onOpenDialog, dataItemKey],
  );

  // Memoize the MultiSelect props to prevent unnecessary re-renders
  const multiSelectProps = useMemo(
    () => ({
      ...fieldRest,
      ...rest,
      data: filterable ? filteredData : data,
      name,
      id,
      textField: textField as string,
      value: value ?? [],
      dataItemKey: dataItemKey as string,
      onChange: handleChange,
      onBlur: handleBlur,
      onFilterChange: filterable ? handleFilterChange : undefined,
      filterable,
      tagRender: enableCustomTagRender ? tagRender : undefined,
      disabled,
      valid: !invalid,
      validationMessage: error?.message,
    }),
    [
      fieldRest,
      rest,
      filterable,
      filteredData,
      data,
      name,
      id,
      textField,
      value,
      dataItemKey,
      handleChange,
      handleBlur,
      handleFilterChange,
      enableCustomTagRender,
      tagRender,
      disabled,
      invalid,
      error?.message,
    ],
  );

  return (
    <StyledContainerDiv>
      <MultiSelect {...multiSelectProps} />
    </StyledContainerDiv>
  );
};

const StyledContainerDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
